import type { ViewTab } from "../types";

import { SkeletonLoader } from "../../SkeletonLoader";

/** Rows to fill the skeleton list with, matching the canvas's 7. */
const SKELETON_ROW_COUNT = 7;

/**
 * The row's leading element mirrors the loaded mobile `SourceCell` for the
 * active tab: a round 28px flag for Country, nothing for the name-only Rebate
 * program and Campaign tabs, and the 40×34 icon/thumb tile otherwise.
 */
const RowLead = ({ tab }: { tab: ViewTab }) => {
  if (tab === "country")
    return (
      <SkeletonLoader
        variant="rounded"
        motionReduce
        width={28}
        height={28}
        radius="full"
      />
    );
  if (tab === "rebate" || tab === "campaign") return null;
  return (
    <SkeletonLoader
      variant="rounded"
      motionReduce
      width={40}
      height={34}
      radius="sm"
    />
  );
};

/**
 * Mobile row-list loading skeleton. Deliberately not `TableMobileSkeletonLoader`
 * — that one renders bordered, rounded cards shaped like the Clients list,
 * whereas this report's loaded rows are a flush, divider-separated list, so the
 * shared loader visibly reflows the page when data arrives.
 *
 * Rows only: the column header is rendered by the page (`MobileRowListHeader`)
 * outside the state branch, so it stays live here rather than being skeletoned.
 *
 * Row height and dividers mirror `MobileRowList`'s rows (`py-3.5`, bottom
 * `stroke-primary` divider on every row including the last).
 */
export const MobileRowListSkeleton = ({ tab }: { tab: ViewTab }) => (
  <div className="flex flex-col" data-testid="mobile-row-list-skeleton">
    {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
      <div
        key={index}
        className="flex min-h-11 w-full items-center gap-2 border-b border-stroke-primary py-3.5"
      >
        <RowLead tab={tab} />
        <div className="min-w-0 flex-1">
          <SkeletonLoader
            variant="rounded"
            motionReduce
            radius="md"
            width="60%"
            height={13}
          />
        </div>
        <SkeletonLoader
          variant="rounded"
          motionReduce
          radius="md"
          width={44}
          height={13}
        />
      </div>
    ))}
  </div>
);
